import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from '../api/axios';
import { API_ENDPOINTS } from "../api/endpoint";
import { toast } from 'react-toastify';

const initialState = {
    chatSettings: {
        display_mode: 'home_only' // default: home_only, all_pages, sidebar
    },
    loading: false,
    error: null
};

// Get chat settings (public)
export const getChatSettings = createAsyncThunk(
    'chatSettings/getChatSettings',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios({
                url: API_ENDPOINTS.GET_CHAT_SETTINGS,
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            return response.data?.settings || { display_mode: 'home_only' };
        } catch (error) {
            console.error('Failed to get chat settings:', error);
            return rejectWithValue(error.response?.data || { error: 'Failed to get chat settings' });
        }
    }
);

// Admin: Get chat settings
export const adminGetChatSettings = createAsyncThunk(
    'chatSettings/adminGetChatSettings',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios({
                url: API_ENDPOINTS.ADMIN_GET_CHAT_SETTINGS,
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            return response.data?.settings || { display_mode: 'home_only' };
        } catch (error) {
            toast.error(error?.response?.data?.error || 'Failed to get chat settings!');
            return rejectWithValue(error.response?.data || { error: 'Failed to get chat settings' });
        }
    }
);

// Admin: Update chat settings
export const adminUpdateChatSettings = createAsyncThunk(
    'chatSettings/adminUpdateChatSettings',
    async (data, { rejectWithValue }) => {
        try {
            const response = await axios({
                url: API_ENDPOINTS.ADMIN_UPDATE_CHAT_SETTINGS,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                data: data
            });
            toast.success(response.data?.message || 'Chat settings updated successfully!');
            return response.data?.settings || { display_mode: data.display_mode };
        } catch (error) {
            toast.error(error?.response?.data?.error || 'Failed to update chat settings!');
            return rejectWithValue(error.response?.data || { error: 'Failed to update chat settings' });
        }
    }
);

export const chatSettingsSlice = createSlice({
    name: 'chatSettings',
    initialState,
    reducers: {
        resetChatSettings: (state) => {
            state.chatSettings = { display_mode: 'home_only' };
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Get chat settings (public)
            .addCase(getChatSettings.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getChatSettings.fulfilled, (state, action) => {
                state.loading = false;
                state.chatSettings = action.payload;
            })
            .addCase(getChatSettings.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Admin: Get chat settings
            .addCase(adminGetChatSettings.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(adminGetChatSettings.fulfilled, (state, action) => {
                state.loading = false;
                state.chatSettings = action.payload;
            })
            .addCase(adminGetChatSettings.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Admin: Update chat settings
            .addCase(adminUpdateChatSettings.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(adminUpdateChatSettings.fulfilled, (state, action) => {
                state.loading = false;
                state.chatSettings = action.payload;
            })
            .addCase(adminUpdateChatSettings.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

export const { resetChatSettings } = chatSettingsSlice.actions;
export default chatSettingsSlice.reducer;

